import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom";
  className?: string;
}

/**
 * Lightweight tooltip — shows on hover AND keyboard focus (a11y), describes the trigger via
 * aria-describedby. The bubble is PORTALED to <body> and positioned as `fixed` off the trigger's
 * rect, so it escapes any ancestor's overflow:hidden / overflow-auto clipping (e.g. inside Table's
 * scroll container) — the classic clipped-tooltip bug. aria-describedby is an IDREF, so the
 * trigger→bubble association survives the portal. The layer uses the semantic --z-tooltip token.
 */
export function Tooltip({ content, children, side = "top", className }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Measure before paint so the bubble appears already-positioned (no first-frame jump).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ top: side === "top" ? r.top : r.bottom, left: r.left + r.width / 2 });
  }, [open, side]);

  return (
    <span
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span aria-describedby={open ? id : undefined}>{children}</span>
      {open &&
        coords &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: "var(--z-tooltip)",
              transform:
                side === "top"
                  ? "translate(-50%, calc(-100% - 0.375rem))"
                  : "translate(-50%, 0.375rem)",
            }}
            className={cn(
              "pointer-events-none whitespace-nowrap " +
                "rounded-(--radius-sm) bg-(--text-strong) px-2.5 py-1.5 " +
                "text-(length:--text-xs) font-medium text-(--surface) shadow-(--shadow-md) " +
                "animate-[toast-in_var(--dur-fast)_var(--ease-out)] motion-reduce:animate-none",
              className,
            )}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
